import React from 'react';
import PropTypes from 'prop-types';
import { Link } from '@resy/ui-kitchen';
import { sanitizeString } from '../../../../../../ui-kitchen/src/utils/sanitizeString';
import {
  filterArrayWithMandatoryItems,
  checkJsonObjectMandatory,
  getRetinaImage,
  handleMktAnalytics,
} from '../../AmexMarketingUtils/AmexUtils';
import { CTA_EVENT_NAME } from '../../AmexMarketingUtils/constants';
import './AmexResyValueRelationship.scss';

const AmexResyValueRelationship = (
  {
    sectionConfig = {},
    Analytics,
    baseRequestUrl,
    awsFolderName,
    matchMedia,
  }
) => {
  const isMobileUX = !matchMedia.md;

  if (!sectionConfig || Object.keys(sectionConfig).length === 0) return null;

  const requiredKeysMain = ['title', 'copy', 'sections', 'banner'];
  const isValidConfig = checkJsonObjectMandatory(requiredKeysMain, sectionConfig, 'relationship');
  if (!isValidConfig) return null;

  const requiredKeysBanner = ['fine_copy', 'logo_cobrand', 'images'];
  const isValidBanner = checkJsonObjectMandatory(requiredKeysBanner, sectionConfig?.banner, 'relationship');
  if (!isValidBanner) return null;

  const requiredKeys = ['title', 'copy', 'cta', 'fine_print'];
  const filteredSections = filterArrayWithMandatoryItems(requiredKeys, sectionConfig?.sections, 'relationship');
  if (filteredSections?.length === 0) return null;

  const {
    fine_copy: fineCopy = '',
    images: {
      desktop: desktopImg = {},
      mobile: mobileImg = {},
    } = {},
    logo_cobrand: logoCobrand,
  } = sectionConfig?.banner;

  const sanitizedFineCopy = sanitizeString(fineCopy);
  const sanitizedSectionTitle = sanitizeString(sectionConfig?.title);
  const sanitizedSectionCopy = sanitizeString(sectionConfig?.copy);

  const urlPrefix = `${baseRequestUrl}${awsFolderName}/images/`;
  const bannerImgName = getRetinaImage(desktopImg, mobileImg, !isMobileUX);
  const bannerImg = `${urlPrefix}${bannerImgName}`;
  const logoResy = getRetinaImage(logoCobrand, logoCobrand, true);
  const sectionId = sectionConfig?.section_id;

  const displayCTA = (itemContent) => {
    const { cta } = itemContent;
    const {
      label, url,
    } = cta;
    if (Object.keys(cta).length === 0 || !label) return null;

    return (
      <Link
        rel="noopener noreferrer"
        target="_blank"
        href={`${url}`}
        className="cta"
        onClick={() => {
          const extraParams = {
            action_name: 'Learn More Click',
            item_name: itemContent?.title,
          };
          handleMktAnalytics(Analytics?.event, CTA_EVENT_NAME, extraParams);
        }}
      >
        {label}
      </Link>
    );
  };

  const renderBannerContent = () => (
    <>
      <div className="AmexResyValueRelationship__banner__content">
        {logoResy && (
        <img
          className="AmexResyValueRelationship__banner__content--logo--resy"
          src={`${urlPrefix}${logoResy}`}
          alt="Resy Amex logo"
        />
        )}
        {!isMobileUX && fineCopy && (
        <p
          id="disclaimer"
          className="AmexResyValueRelationship__banner__textprint"
                  // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: sanitizedFineCopy,
          }}
        />
        )}
      </div>
      {isMobileUX && (
        <div className="AmexResyValueRelationship__banner__image-wrapper">
          <img
            className="AmexResyValueRelationship__banner__image-mobile"
            src={bannerImg}
            alt=""
          />

          {fineCopy && (
            <div className="AmexResyValueRelationship__banner__copy-wrapper">
              <p
                id="disclaimer"
                className="AmexResyValueRelationship__banner__textprint"
                  // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: sanitizedFineCopy,
                }}
              />
            </div>
          )}
        </div>
      )}
    </>
  );

  const renderItemContent = (itemContent) => {
    const sanitizedItemTitle = sanitizeString(itemContent?.title);
    const sanitizedItemCopy = sanitizeString(itemContent?.copy);
    const sanitizedFinePrint = sanitizeString(itemContent?.fine_print);
    return (
      <>
        <div className="AmexResyValueRelationship__itemContent__copy">
          { itemContent?.title && (
            <h4
              className="AmexResyValueRelationship__itemContent__title"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: sanitizedItemTitle,
              }}
            />
          ) }
          { itemContent?.copy && (
            <p
              className="AmexResyValueRelationship__itemContent__content"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: sanitizedItemCopy,
              }}
            />
          )}

          {itemContent?.fine_print && (
            <p
              className="AmexResyValueRelationship__itemContent__fineprint"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: sanitizedFinePrint,
              }}
            />
          )}
        </div>

        { itemContent?.cta && (
          <div className="AmexResyValueRelationship__itemContent__cta">
            {displayCTA(itemContent)}
          </div>
        )}
      </>
    );
  };

  return (
    <section className="AmexResyValueRelationship">
      <div className="container">
        <div className="AmexResyValueRelationship__banner__wrapper">
          <div
            className="AmexResyValueRelationship__banner__image"
            style={{ backgroundImage: `url(${bannerImg})` }}
          />
          {renderBannerContent()}
        </div>
        {sectionConfig?.title && (
          <h2
            id={sectionId}
            aria-describedby="disclaimer"
            className="AmexResyValueRelationship__title heading-2"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: sanitizedSectionTitle,
            }}
          />
        )}
        {sectionConfig?.copy && (
        <h3
          className="AmexResyValueRelationship__content"
              // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: sanitizedSectionCopy,
          }}
        />
        )}
        <ul className="AmexResyValueRelationship__grid">
          {filteredSections.map((itemSection) => (
            <li className="AmexResyValueRelationship__itemContent" key={itemSection?.title}>
              {renderItemContent(itemSection)}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

AmexResyValueRelationship.propTypes = {
  sectionConfig: PropTypes.shape({
    title: PropTypes.string,
    section_id: PropTypes.string,
    image: PropTypes.shape({
      desktop: PropTypes.string.isRequired,
      mobile: PropTypes.string.isRequired,
    }),
    cta: PropTypes.shape({}),
  }).isRequired,
  baseRequestUrl: PropTypes.string.isRequired,
  awsFolderName: PropTypes.string.isRequired,
  banner: PropTypes.shape({
    fine_copy: PropTypes.string.isRequired,
    images: PropTypes.shape({
      desktop: PropTypes.shape({
        regular: PropTypes.string.isRequired,
        retina: PropTypes.string.isRequired,
      }).isRequired,
      mobile: PropTypes.shape({
        regular: PropTypes.string.isRequired,
        retina: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
    logo_cobrand: PropTypes.shape({
      regular: PropTypes.string.isRequired,
      retina: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  matchMedia: PropTypes.shape({
    md: PropTypes.bool.isRequired,
  }).isRequired,
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
};

export default AmexResyValueRelationship;
